/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Hook for Dynamic HTML5 History Routing in Admin Portal (Task 1.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { useState, useEffect, useCallback } from 'react';
import type { AdminViewId } from '../components/layout/AdminSidebar';
import {
  resolvePathToView,
  resolveViewToPath,
  isValidAdminPath,
  ADMIN_ROUTES,
} from '../routes/admin-routes';

export interface UseAdminRoutingResult {
  activeView: AdminViewId;
  currentPath: string;
  navigateTo: (target: AdminViewId | string, replace?: boolean) => void;
  /**
   * Backward-compatible setter for AdminLayout onViewChange callback.
   */
  setActiveView: (view: AdminViewId) => void;
}

/**
 * Normalizes and extracts initial view from browser location on initial load.
 */
function getInitialAdminView(): AdminViewId {
  if (typeof window === 'undefined') return 'dashboard';
  return resolvePathToView(window.location.pathname);
}

/**
 * Normalizes initial pathname.
 */
function getInitialAdminPath(): string {
  if (typeof window === 'undefined') return '/dashboard';
  const pathname = window.location.pathname;
  if (!pathname || pathname === '/' || pathname === '') {
    return '/dashboard';
  }
  return pathname;
}

/**
 * Manages two-way synchronization between browser URL pathname (HTML5 History API)
 * and active backoffice module state.
 */
export function useAdminRouting(): UseAdminRoutingResult {
  const [activeView, setActiveViewInternal] = useState<AdminViewId>(getInitialAdminView);
  const [currentPath, setCurrentPath] = useState<string>(getInitialAdminPath);

  /**
   * Unified navigation function supporting both AdminViewId and URL pathnames.
   * Automatically preserves active query parameters (e.g. ?template=cyberpunk-noc).
   */
  const navigateTo = useCallback((target: AdminViewId | string, replace = false) => {
    let targetView: AdminViewId = 'dashboard';
    let targetPath = '/dashboard';

    // 1. Determine whether target is an AdminViewId or a path string
    const isDirectViewId = ADMIN_ROUTES.some((r) => r.id === target);

    if (isDirectViewId) {
      targetView = target as AdminViewId;
      targetPath = resolveViewToPath(targetView);
    } else {
      const cleanPath = typeof target === 'string' ? target.split('?')[0]?.split('#')[0] || '/dashboard' : '/dashboard';
      targetView = resolvePathToView(cleanPath);
      targetPath = isValidAdminPath(cleanPath) ? cleanPath : resolveViewToPath(targetView);
    }

    setActiveViewInternal(targetView);
    setCurrentPath(targetPath);

    // 2. Synchronize with HTML5 History API while preserving search query params
    if (typeof window !== 'undefined') {
      const search = window.location.search || '';
      const fullUrl = `${targetPath}${search}`;

      if (replace) {
        window.history.replaceState({ viewId: targetView }, '', fullUrl);
      } else if (window.location.pathname !== targetPath) {
        window.history.pushState({ viewId: targetView }, '', fullUrl);
      }
    }
  }, []);

  /**
   * Backward-compatible alias for onViewChange prop.
   */
  const setActiveView = useCallback(
    (view: AdminViewId) => {
      navigateTo(view);
    },
    [navigateTo]
  );

  // 3. Listen for popstate event (Browser Back / Forward buttons)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = (event: PopStateEvent) => {
      const newPath = window.location.pathname;
      const stateViewId = event.state?.viewId as AdminViewId | undefined;

      const nextView = stateViewId && ADMIN_ROUTES.some((r) => r.id === stateViewId)
        ? stateViewId
        : resolvePathToView(newPath);

      setActiveViewInternal(nextView);
      setCurrentPath(newPath);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // 4. Initial path canonicalization on mount if hitting root '/'
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const initial = window.location.pathname;
    if (initial === '/' || initial === '') {
      const search = window.location.search || '';
      window.history.replaceState({ viewId: 'dashboard' }, '', `/dashboard${search}`);
      setCurrentPath('/dashboard');
    }
  }, []);

  return {
    activeView,
    currentPath,
    navigateTo,
    setActiveView,
  };
}

